import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lock } from "lucide-react";
import { FadeUp, MaskLine } from "./Reveal";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export const Killhouse = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
    <section id="killhouse" ref={ref} data-testid="killhouse-locked-section" className="group relative overflow-hidden border-b border-[var(--line)]">
      <motion.img style={{ y }} src={useSite().img("killhouse")} alt="" aria-hidden="true"
        className="absolute -inset-y-[15%] w-full h-[130%] object-cover object-[50%_40%] grayscale brightness-[0.3] blur-[2px] group-hover:grayscale-0 transition-[filter] duration-1000" />
      <div className="absolute inset-0 bg-black/50" />
      <div className="absolute inset-0 grid-lines opacity-[0.1]" />

      <div className="relative z-10 px-5 sm:px-8 lg:px-12 py-28 sm:py-40 flex flex-col items-center text-center">
        <FadeUp>
          <span data-testid="killhouse-lock-badge" className="inline-flex items-center gap-3 border border-white/30 bg-black/60 backdrop-blur-md px-4 py-2.5 font-mono text-xs uppercase tracking-[0.3em]">
            <Lock className="w-4 h-4" /> {t("kh.locked")} <span className="w-1.5 h-1.5 bg-white blink" />
          </span>
        </FadeUp>
        <h2 className="font-stencil font-black uppercase leading-[0.82] text-[16vw] sm:text-[12vw] lg:text-[10vw] mt-8">
          <MaskLine>{t("kh.title")[0]}</MaskLine>
          <MaskLine delay={0.15} className="text-outline">{t("kh.title")[1]}</MaskLine>
        </h2>
        <FadeUp delay={0.2}>
          <p className="max-w-xl mx-auto mt-8 text-base text-[var(--bone)]/85 leading-relaxed">
            {t("kh.text")}
          </p>
        </FadeUp>

        <div aria-hidden="true" className="mt-16 w-full max-w-5xl grid sm:grid-cols-3 border border-white/15 bg-black/50 backdrop-blur-md select-none pointer-events-none">
          {t("kh.teasers").map(([title, d], i) => (
            <div key={i} className="p-6 sm:p-8 text-left border-b sm:border-b-0 sm:border-r last:border-0 border-white/15">
              <span className="font-mono text-xs text-[var(--muted)]">0{i + 1}</span>
              <p className="font-display text-2xl font-bold uppercase mt-2 blur-[5px] opacity-60">{title}</p>
              <p className="text-sm text-[var(--muted)] mt-2 blur-[4px] opacity-60">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
